import {getCookie, setCookie, deleteCookie} from './cookie';
import {isArray} from './boolean';
import {KEY_TRUSTED_DEVICE_TOKEN} from '../constants/general';
import {
  TRUSTED_DEVICE_TOKEN_DAYS,
  TWO_FACTOR_CHANNEL_EMAIL,
  TWO_FACTOR_CHANNEL_SMS,
  TWO_FACTOR_CHANNEL_TOTP,
  TWO_FACTOR_CODE_LENGTH,
} from '../constants/twoFactor';

/**
 * True for authenticator apps, where no code is sent anywhere.
 *
 * @param {string} channel
 * @returns {boolean}
 */
export const isGeneratedTwoFactorChannel = channel =>
  channel === TWO_FACTOR_CHANNEL_TOTP;

/**
 * Heading for the code entry step.
 *
 * @param {string} channel
 * @returns {string}
 */
export const getTwoFactorHeading = channel => {
  if (isGeneratedTwoFactorChannel(channel)) {
    return 'Enter the code from your app';
  }
  if (channel === TWO_FACTOR_CHANNEL_EMAIL) {
    return 'Check your email';
  }
  return 'Enter the code';
};

/**
 * Tells the user where the code went. `destination` comes back masked from
 * the API, and is null for authenticator apps.
 *
 * @param {{channel: string, destination: ?string}} challenge
 * @returns {string}
 */
export const getTwoFactorIntro = ({channel, destination} = {}) => {
  if (isGeneratedTwoFactorChannel(channel)) {
    return `Open your authenticator app and enter the ${TWO_FACTOR_CODE_LENGTH}-digit code it is showing for your Tenant Profile.`;
  }
  if (channel === TWO_FACTOR_CHANNEL_SMS) {
    return destination
      ? `We have sent a ${TWO_FACTOR_CODE_LENGTH}-digit code by text message to ${destination}.`
      : `We have sent you a ${TWO_FACTOR_CODE_LENGTH}-digit code by text message.`;
  }
  return destination
    ? `We have sent a ${TWO_FACTOR_CODE_LENGTH}-digit code to ${destination}.`
    : `We have sent you a ${TWO_FACTOR_CODE_LENGTH}-digit code.`;
};

/**
 * The token marking this browser as already verified, if we hold one.
 *
 * @returns {?string}
 */
export const getTrustedDeviceToken = () =>
  getCookie(KEY_TRUSTED_DEVICE_TOKEN) || null;

/**
 * Remember this browser.
 *
 * @param {?string} token
 * @returns {void}
 */
export const storeTrustedDeviceToken = token => {
  if (!token || typeof token !== 'string') {
    return;
  }
  const expiry = new Date(
    Date.now() + TRUSTED_DEVICE_TOKEN_DAYS * 24 * 60 * 60 * 1000
  ).toUTCString();
  setCookie(KEY_TRUSTED_DEVICE_TOKEN, token, expiry);
};

/**
 * Forget this browser. Not part of signing out.
 *
 * @returns {void}
 */
export const clearTrustedDeviceToken = () =>
  deleteCookie(KEY_TRUSTED_DEVICE_TOKEN);

const firstMessage = value => (isArray(value) ? value.join(' ') : value);

/**
 * A 429 from the login is either a credential lockout or too many challenges,
 * told apart by the key the API puts the error under.
 *
 * @param {{errors: ?Object, message: ?string}} error
 * @returns {string}
 */
export const getLoginRateLimitMessage = ({errors, message} = {}) => {
  const {challenge, username} = errors || {};
  if (username) {
    // carries the wait time
    return firstMessage(username);
  }
  if (challenge) {
    return (
      firstMessage(challenge) ||
      'Too many verification codes have been requested for this account. Please wait a while before trying again.'
    );
  }
  return message || 'Too many attempts. Please wait a moment and try again.';
};
